import { html, property, state } from 'lit-element';
import { ifDefined } from 'lit-html/directives/if-defined';
import { nothing } from 'lit-html';
import {
  KatLitElement,
  EventEmitter,
  event,
  register,
  Keys,
} from '../../shared/base';
import { checkSlots } from '../../shared/slot-utils';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './panel.lit.scss';
import getString from './strings';

/**
 * @component {kat-panel} KatalPanel The panel is a content container that exists outside the main page structure, like a modal. It provides a space for contextual information and/or actions that would otherwise clutter the page if included in the main body of the page. The panel will remain active until the user explicitly chooses to dismiss it by clicking the close button or completing an action.
 * @status Production
 * @slot default The body content of the panel.
 * @slot header The title of the panel.
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-panel')
export class KatPanel extends KatLitElement {
  static get styles() {
    return [baseStyles, styles];
  }

  /** Determines if the panel is open or closed. Defaults to false. */
  @property()
  visible = false;

  /**
   * Ensures that the public .open()/.close() APIs don't trigger themselves after their reflected property updates.
   * https://sim.amazon.com/issues/KAT-11702
   */
  @state()
  _ignoreNextVisibleChange = false;

  /** Whether the panel can be dismissed. When true, the panel will render without a close button. Defaults to false. */
  @property()
  persistent?: boolean;

  /** Title text. Displayed on the same axis as the 'close' button. */
  @property()
  header?: string;

  /** Defines the label read to screen-reader users */
  @property({ attribute: 'kat-aria-label' })
  katAriaLabel? = 'panel';

  /**
   * Disables open/close animations when true. Useful for panels that are open immediately when a page renders.
   * Defaults to false.
   */
  @property({ attribute: 'disable-animation' })
  disableAnimation = false;

  /** Emits when the panel dialog is opened. */
  @event('open', { cancelable: true, bubbles: true })
  private _open: EventEmitter<{ visible: boolean }>;

  /** Emits when the panel dialog is closed. */
  @event('close', { cancelable: true, bubbles: true })
  private _close: EventEmitter<{ visible: boolean }>;

  // Propagate attribute changes to parent PanelWrapper
  updated(changedProperties: Map<string, any>) {
    if (this._ignoreNextVisibleChange) {
      this._ignoreNextVisibleChange = false;
      return;
    }

    if (
      changedProperties.has('visible') &&
      changedProperties.get('visible') !== undefined
    ) {
      if (this.visible) {
        this.open();
      } else {
        this.close();
      }
    }
  }

  async open() {
    const canceled = !this._open.emit({ visible: true });
    if (canceled) {
      return;
    }

    this._ignoreNextVisibleChange = true;
    this.visible = true;
    await this.updateComplete;
  }

  async close() {
    const canceled = !this._close.emit({ visible: false });

    if (canceled) {
      return;
    }

    this._ignoreNextVisibleChange = true;
    this.visible = false;
    await this.updateComplete;
  }

  handleKeyDown(e) {
    if (e.keyCode === Keys.Enter || e.keyCode === Keys.Space) {
      this.close();
    }
  }

  renderHeader() {
    const renderedHeader = checkSlots(this).header
      ? html`<slot name="header"></slot>`
      : this.header;

    const renderedCloseButton = this.persistent
      ? nothing
      : html`<button
          class="close"
          type="button"
          @click=${this.close}
          @keydown=${this.handleKeyDown}
          part="panel-close-button"
          aria-label="${getString('kat-panel-close')}"
        >
          <kat-icon name="exit" size="small"></kat-icon>
        </button>`;

    return html`
      <div class="header">${renderedHeader} ${renderedCloseButton}</div>
    `;
  }

  render() {
    return html`
      <div
        class="container"
        role="dialog"
        aria-label=${ifDefined(this.katAriaLabel)}
        aria-live="polite"
      >
        ${this.renderHeader()}
        <div class="content">
          <slot></slot>
        </div>
      </div>
    `;
  }
}
